<template>
  <div v-if="isReady && product" class="min-h-screen">
    <component
      :is="pdpComponent"
      :base-product="product"
      :product-id="productId"
    />
  </div>
  <div v-else class="mx-auto max-w-7xl"></div>
</template>

<script setup lang="ts">
import { getPDPComponentType } from '~/utils/pdp'
import { isProductValid } from '~/utils/product'
import { PDPComponentType } from '~/types/pdp'
// Need to import these explicitly
import GlassesPdp from '~/pages/p/glasses-pdp.vue'
import RxGlassesPdp from '~/pages/p/rx-glasses-pdp.vue'
import MerchPdp from '~/pages/p/merch-pdp.vue'
import ContactLensPdp from '~/pages/p/contact-lens-pdp.vue'
import CleaningFluidPdp from '~/pages/p/cleaning-fluid-pdp.vue'
import HearingAidsMerchPdp from '~/pages/p/hearing-aids-merch-pdp.vue'
// Need to import these explicitly

const fimLocalePath = useFimLocalePath()
const { setPageState } = usePageState()
const scope = effectScope()
onScopeDispose(() => scope.stop())
const route = useRoute()
const { getProductDetailRoute } = useRouteHelpers()
// Multilangual stuff
const { isInEditorMode } = useStoryblokHelpers()
const $currentShop = useCurrentShop()
const { configuredLocale, isMultilanguageShop, redirectToLocalePage } =
  useLocale()
const getRouteBaseName = useRouteBaseName()
const productId = computed(() => {
  const id = String(route.params.slug).split('-').pop()
  return typeof id !== 'undefined' ? parseInt(id, 10) : 0
})

/**
 * This watch must be created before any 'await'.
 * If its created after 'await', it must be stopped manually. (https://vuejs.org/guide/essentials/watchers.html#stopping-a-watcher)
 * However, while SSR we do not have the right hook for that.
 */
scope.run(() => {
  if (import.meta.server) {
    return
  }
  if (isMultilanguageShop.value || !isInEditorMode.value) {
    const trueRouteName = getRouteBaseName(route)
    watch(
      configuredLocale,
      async (newLang) => {
        if (
          $currentShop.value.locale === newLang ||
          trueRouteName !== 'p-slug'
        ) {
          return
        }
        const query = route.fullPath.split('?')[1]
        const path = `/p/${productId.value}/`
        await redirectToLocalePage(path, query)
      },
      { immediate: true },
    )
  }
})

const {
  data: product,
  error,
  fetching,
} = await useProduct({
  key: `product-base-${productId.value}`,
  params: {
    id: productId.value,
    includeSellableForFree: true,
    includeSoldOut: true,
    with: SAFE_PRODUCT_WITH,
  },
})

// Hide PDPs of rx config items
const isRxLens = computed(() =>
  rxLensProductMasterKeys.includes(product.value?.masterKey || ''),
)

if (error.value || !isProductValid(product.value) || isRxLens.value) {
  setPageState('pageType', 'error')
  throw createError({
    statusCode: 404,
    statusMessage: `Not found`,
  })
}

const pdpComponent = computed(() => {
  if (!isProductValid(product.value) || isRxLens.value) {
    return undefined
  }

  const componentsMap = {
    [PDPComponentType.glasses]: GlassesPdp,
    [PDPComponentType.correctionalGlasses]: RxGlassesPdp,
    [PDPComponentType.merch]: MerchPdp,
    [PDPComponentType.contactLens]: ContactLensPdp,
    [PDPComponentType.care]: CleaningFluidPdp,
    [PDPComponentType.hearingAidsMerch]: HearingAidsMerchPdp,
  }

  return componentsMap[getPDPComponentType(unref(product))]
})

const isReady = computed(() => {
  return Boolean(
    (fetching.value || product.value) && pdpComponent && !isRxLens.value,
  )
})

useHead(() => {
  if (!product.value) {
    return {}
  }
  return {
    link: [
      {
        rel: 'canonical',
        key: 'canonical',
        href: sanitizeCanonical(
          getUrl(
            $currentShop.value.domain,
            fimLocalePath(getProductDetailRoute(product.value)),
          ),
        ),
      },
    ],
  }
})

defineOptions({ name: 'ProductDetailPage' })
</script>
